import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { API_URL } from '@/utils/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Settings as SettingsIcon, RefreshCw, Clock, AlertTriangle, Save,
  UserX, Timer, Camera, Trash2, Shield, Calculator, Info, Zap, Lock
} from 'lucide-react';

const API = API_URL;

function ImpactBadge({ type, fr }) {
  const configs = {
    payroll: { label: fr ? 'Impact paie' : 'Payroll impact', cls: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-400 border-purple-200 dark:border-purple-800' },
    presence: { label: fr ? 'Impact présence' : 'Presence impact', cls: 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-400 border-blue-200 dark:border-blue-800' },
    security: { label: fr ? 'Impact sécurité' : 'Security impact', cls: 'bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 border-red-200 dark:border-red-800' },
  };
  const c = configs[type] || configs.presence;
  return <Badge className={`${c.cls} border text-[9px] gap-0.5`}><Zap size={8} /> {c.label}</Badge>;
}

export default function SettingsPage() {
  const { t, language } = useLanguage();
  const { user } = useAuth();
  const { effectiveTenantId, loading: tenantLoading } = useTenant();
  const fr = language === 'fr';

  const [settings, setSettings] = useState({
    standardBreakDuration: 30, lateToleranceMinutes: 5, lateDelayMinutes: 5,
    absenceDelayMinutes: 30, photoRetentionDays: 30,
    payPeriodType: 'biweekly', payPeriodStartDay: 0, payPeriodStartDate: 1,
    overtimeRule: 'weekly', dailyOvertimeThreshold: 8, weeklyOvertimeThreshold: 40,
    validationDeadlineDays: 1
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    if (!effectiveTenantId) return;
    try { const response = await axios.get(`${API}/settings/${effectiveTenantId}`); setSettings(response.data); }
    catch (error) { console.error('Failed to fetch settings:', error); }
    finally { setLoading(false); }
  }, [effectiveTenantId]);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    if (!effectiveTenantId) return;
    setSaving(true);
    try {
      await axios.put(`${API}/settings/${effectiveTenantId}`, {
        standardBreakDuration: parseInt(settings.standardBreakDuration) || 30,
        lateToleranceMinutes: parseInt(settings.lateToleranceMinutes) || 5,
        lateDelayMinutes: parseInt(settings.lateDelayMinutes) || 5,
        absenceDelayMinutes: parseInt(settings.absenceDelayMinutes) || 30,
        photoRetentionDays: parseInt(settings.photoRetentionDays) || 30,
        payPeriodType: settings.payPeriodType || 'biweekly',
        payPeriodStartDay: parseInt(settings.payPeriodStartDay) || 0,
        payPeriodStartDate: parseInt(settings.payPeriodStartDate) || 1,
        overtimeRule: settings.overtimeRule || 'weekly',
        dailyOvertimeThreshold: parseFloat(settings.dailyOvertimeThreshold) || 8,
        weeklyOvertimeThreshold: parseFloat(settings.weeklyOvertimeThreshold) || 40,
        validationDeadlineDays: parseInt(settings.validationDeadlineDays) || 1
      });
      toast.success(fr ? 'Paramètres enregistrés' : 'Settings saved');
    } catch (error) { toast.error(fr ? 'Erreur' : 'Error'); }
    finally { setSaving(false); }
  };

  if (loading || tenantLoading) return <div className="flex items-center justify-center h-64"><RefreshCw className="animate-spin text-primary" size={32} /></div>;

  return (
    <div className="space-y-4" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-slate-900 dark:text-white">{fr ? 'Paramètres' : 'Settings'}</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">{fr ? 'Configuration de l\'entreprise' : 'Company configuration'}</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="gap-1.5 h-8" data-testid="save-settings-btn">
          {saving ? <RefreshCw className="animate-spin" size={14} /> : <Save size={14} />}
          {fr ? 'Enregistrer' : 'Save'}
        </Button>
      </div>

      {/* ========== SECTION 1: TEMPS ========== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Clock size={16} className="text-blue-600 dark:text-blue-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{fr ? 'Temps' : 'Time'}</h2>
        </div>

        <Card className="dark:border-slate-700" data-testid="time-settings">
          <CardContent className="p-4 space-y-4">
            {/* Break & Tolerance */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{fr ? 'Durée de pause standard' : 'Standard break duration'}</Label>
                  <ImpactBadge type="presence" fr={fr} />
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="number" min="0" max="120" value={settings.standardBreakDuration} onChange={e => setSettings({ ...settings, standardBreakDuration: e.target.value })} className="w-24 h-8 text-sm" data-testid="break-duration-input" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">min</span>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">{fr ? 'Durée de pause déduite automatiquement' : 'Auto-deducted break duration'}</p>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{fr ? 'Tolérance retard' : 'Late tolerance'}</Label>
                  <ImpactBadge type="presence" fr={fr} />
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="number" min="0" max="60" value={settings.lateToleranceMinutes} onChange={e => setSettings({ ...settings, lateToleranceMinutes: e.target.value })} className="w-24 h-8 text-sm" data-testid="late-tolerance-input" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">min</span>
                </div>
                <p className="text-[10px] text-slate-500 dark:text-slate-400">{fr ? 'Marge avant de marquer un retard' : 'Grace period before marking late'}</p>
              </div>
            </div>

            <hr className="dark:border-slate-700" />

            {/* Alert delays */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs flex items-center gap-1"><Timer size={12} className="text-amber-600" /> {fr ? 'Délai de retard' : 'Late delay'}</Label>
                  <ImpactBadge type="presence" fr={fr} />
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="number" min="1" max="60" value={settings.lateDelayMinutes} onChange={e => setSettings({ ...settings, lateDelayMinutes: e.target.value })} className="w-24 h-8 text-sm" data-testid="late-delay-input" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">min</span>
                </div>
                <div className="bg-amber-50 dark:bg-amber-950/20 p-2 rounded border border-amber-200 dark:border-amber-800">
                  <p className="text-[10px] text-amber-700 dark:text-amber-400">{fr ? 'Minutes après le début du quart → marqué "en retard"' : 'Minutes after shift start → marked as "late"'}</p>
                </div>
              </div>
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs flex items-center gap-1"><UserX size={12} className="text-red-600" /> {fr ? 'Délai d\'absence' : 'Absence delay'}</Label>
                  <ImpactBadge type="presence" fr={fr} />
                </div>
                <div className="flex gap-2 items-center">
                  <Input type="number" min="5" max="240" value={settings.absenceDelayMinutes} onChange={e => setSettings({ ...settings, absenceDelayMinutes: e.target.value })} className="w-24 h-8 text-sm" data-testid="absence-delay-input" />
                  <span className="text-xs text-slate-500 dark:text-slate-400">min</span>
                </div>
                <div className="bg-red-50 dark:bg-red-950/20 p-2 rounded border border-red-200 dark:border-red-800">
                  <p className="text-[10px] text-red-700 dark:text-red-400">{fr ? 'Minutes sans pointage → marqué "absent"' : 'Minutes without punch → marked "absent"'}</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== SECTION 2: PAIE ========== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Calculator size={16} className="text-purple-600 dark:text-purple-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{fr ? 'Paie' : 'Payroll'}</h2>
        </div>

        <Card className="dark:border-slate-700" data-testid="payroll-settings">
          <CardContent className="p-4 space-y-4">
            {/* Period */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Label className="text-xs">{fr ? 'Type de période de paie' : 'Pay period type'}</Label>
                  <ImpactBadge type="payroll" fr={fr} />
                </div>
                <Select value={settings.payPeriodType} onValueChange={v => setSettings({ ...settings, payPeriodType: v })}>
                  <SelectTrigger className="h-8 text-sm" data-testid="pay-period-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="weekly">{fr ? 'Hebdomadaire' : 'Weekly'}</SelectItem>
                    <SelectItem value="biweekly">{fr ? 'Aux deux semaines' : 'Bi-weekly'}</SelectItem>
                    <SelectItem value="monthly">{fr ? 'Mensuelle' : 'Monthly'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">{fr ? 'Jour de début' : 'Start day'}</Label>
                {settings.payPeriodType !== 'monthly' ? (
                  <Select value={String(settings.payPeriodStartDay)} onValueChange={v => setSettings({ ...settings, payPeriodStartDay: parseInt(v) })}>
                    <SelectTrigger className="h-8 text-sm" data-testid="pay-period-start-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {[{v:0,l:'Lundi/Monday'},{v:1,l:'Mardi/Tuesday'},{v:2,l:'Mercredi/Wednesday'},{v:3,l:'Jeudi/Thursday'},{v:4,l:'Vendredi/Friday'},{v:5,l:'Samedi/Saturday'},{v:6,l:'Dimanche/Sunday'}].map(d => (
                        <SelectItem key={d.v} value={String(d.v)}>{d.l.split('/')[fr ? 0 : 1]}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input type="number" min="1" max="28" value={settings.payPeriodStartDate} onChange={e => setSettings({ ...settings, payPeriodStartDate: parseInt(e.target.value) })} className="h-8 text-sm" data-testid="pay-period-start-date" />
                )}
              </div>
            </div>

            <hr className="dark:border-slate-700" />

            {/* Overtime */}
            <div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-3">{fr ? 'Heures supplémentaires' : 'Overtime'}</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <Label className="text-xs">{fr ? 'Règle overtime' : 'Overtime rule'}</Label>
                    <ImpactBadge type="payroll" fr={fr} />
                  </div>
                  <Select value={settings.overtimeRule} onValueChange={v => setSettings({ ...settings, overtimeRule: v })}>
                    <SelectTrigger className="h-8 text-sm" data-testid="overtime-rule"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="daily">{fr ? 'Par jour' : 'Daily'}</SelectItem>
                      <SelectItem value="weekly">{fr ? 'Par semaine' : 'Weekly'}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {settings.overtimeRule === 'daily' ? (
                  <div className="space-y-1.5">
                    <Label className="text-xs">{fr ? 'Seuil quotidien' : 'Daily threshold'}</Label>
                    <div className="flex gap-2 items-center">
                      <Input type="number" step="0.5" min="4" max="12" value={settings.dailyOvertimeThreshold} onChange={e => setSettings({ ...settings, dailyOvertimeThreshold: parseFloat(e.target.value) })} className="w-20 h-8 text-sm" data-testid="daily-threshold" />
                      <span className="text-xs text-slate-500 dark:text-slate-400">h/{fr ? 'jour' : 'day'}</span>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-1.5">
                    <Label className="text-xs">{fr ? 'Seuil hebdomadaire' : 'Weekly threshold'}</Label>
                    <div className="flex gap-2 items-center">
                      <Input type="number" step="0.5" min="20" max="60" value={settings.weeklyOvertimeThreshold} onChange={e => setSettings({ ...settings, weeklyOvertimeThreshold: parseFloat(e.target.value) })} className="w-20 h-8 text-sm" data-testid="weekly-threshold" />
                      <span className="text-xs text-slate-500 dark:text-slate-400">h/{fr ? 'sem' : 'week'}</span>
                    </div>
                  </div>
                )}
                <div className="space-y-1.5">
                  <Label className="text-xs">{fr ? 'Délai validation' : 'Validation deadline'}</Label>
                  <div className="flex gap-2 items-center">
                    <Input type="number" min="1" max="7" value={settings.validationDeadlineDays} onChange={e => setSettings({ ...settings, validationDeadlineDays: parseInt(e.target.value) })} className="w-20 h-8 text-sm" data-testid="validation-deadline" />
                    <span className="text-xs text-slate-500 dark:text-slate-400">{fr ? 'jour(s)' : 'day(s)'}</span>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-purple-50 dark:bg-purple-950/20 p-2.5 rounded-lg border border-purple-200 dark:border-purple-800">
              <p className="text-[10px] text-purple-700 dark:text-purple-400 flex items-start gap-1"><Info size={12} className="shrink-0 mt-0.5" /> {fr ? 'Les heures supplémentaires sont calculées automatiquement lors de la génération des feuilles de temps dans le module Paie.' : 'Overtime is calculated automatically when generating timesheets in the Payroll module.'}</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ========== SECTION 3: SÉCURITÉ ========== */}
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <Shield size={16} className="text-red-600 dark:text-red-400" />
          <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">{fr ? 'Sécurité' : 'Security'}</h2>
        </div>

        <Card className="dark:border-slate-700" data-testid="security-settings">
          <CardContent className="p-4 space-y-4">
            {/* Photo retention */}
            <div className="space-y-1.5">
              <div className="flex items-center gap-2">
                <Label className="text-xs flex items-center gap-1"><Camera size={12} /> {fr ? 'Rétention photos de pointage' : 'Punch photo retention'}</Label>
                <ImpactBadge type="security" fr={fr} />
              </div>
              <div className="flex gap-2 items-center">
                <Input type="number" min="7" max="365" value={settings.photoRetentionDays} onChange={e => setSettings({ ...settings, photoRetentionDays: e.target.value })} className="w-24 h-8 text-sm" data-testid="photo-retention-input" />
                <span className="text-xs text-slate-500 dark:text-slate-400">{fr ? 'jours' : 'days'}</span>
              </div>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">{fr ? 'Les photos sont supprimées après cette période. Les données de pointage sont conservées.' : 'Photos are deleted after this period. Punch data is preserved.'}</p>
            </div>

            <hr className="dark:border-slate-700" />

            {/* Security rules */}
            <div>
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase mb-3">{fr ? 'Règles de sécurité actives' : 'Active security rules'}</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {[
                  { icon: Camera, label: fr ? 'Photo obligatoire à chaque pointage' : 'Photo required for each punch', level: 'strict' },
                  { icon: Timer, label: fr ? 'Anti-spam: 20 secondes entre pointages' : 'Anti-spam: 20 seconds between punches', level: 'strict' },
                  { icon: Clock, label: fr ? 'Horodatage serveur (intégrité)' : 'Server timestamp (integrity)', level: 'strict' },
                  { icon: Shield, label: fr ? 'Journal d\'audit complet' : 'Full audit log', level: 'strict' },
                ].map((rule, i) => (
                  <div key={i} className="flex items-center gap-2 p-2 rounded-lg border dark:border-slate-700 bg-slate-50 dark:bg-slate-800/40">
                    <rule.icon size={14} className="text-green-600 dark:text-green-400 shrink-0" />
                    <span className="text-xs text-slate-700 dark:text-slate-300 flex-1">{rule.label}</span>
                    <Badge className="bg-red-100 dark:bg-red-900/40 text-red-700 dark:text-red-400 text-[8px]">
                      <Lock size={8} className="mr-0.5" /> {rule.level === 'strict' ? (fr ? 'Strict' : 'Strict') : (fr ? 'Flexible' : 'Flexible')}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Company Info */}
      <Card className="dark:border-slate-700" data-testid="company-info">
        <CardHeader className="py-2 px-4 border-b dark:border-slate-700">
          <CardTitle className="text-sm flex items-center gap-2 text-slate-700 dark:text-slate-300"><SettingsIcon size={14} /> {fr ? 'Informations entreprise' : 'Company Info'}</CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[10px] text-slate-500 dark:text-slate-400 uppercase">{fr ? 'ID Tenant' : 'Tenant ID'}</Label>
              <p className="font-mono text-sm text-slate-800 dark:text-slate-200">{effectiveTenantId || user?.tenantId || 'N/A'}</p>
            </div>
            <div>
              <Label className="text-[10px] text-slate-500 dark:text-slate-400 uppercase">{fr ? 'Administrateur' : 'Administrator'}</Label>
              <p className="font-medium text-sm text-slate-800 dark:text-slate-200">{user?.firstName} {user?.lastName}</p>
              <p className="text-[10px] text-slate-500 dark:text-slate-400">{user?.email}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
